import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { Header, Footer } from "../App";
import { MatchCard, AccessoryCard } from "../components/cards";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
      ", " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [history, setHistory] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [owned, setOwned] = useState(new Set());

  const loadHistory = () => {
    setLoadError(false);
    api("/quiz/history").then((res) => {
      setHistory(res.results);
      if (res.results.length) setSelectedId(res.results[0].id);
    }).catch(() => { setHistory([]); setLoadError(true); });
  };

  useEffect(() => {
    loadHistory();
    api("/favorites").then((f) => setFavorites(new Set(f.favorites))).catch(() => {});
    api("/owned").then((o) => setOwned(new Set(o.owned))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFav = (name) => {
    api("/favorites", { method: "POST", body: JSON.stringify({ deck_name: name }) })
      .then((res) => setFavorites(new Set(res.favorites)))
      .catch(() => {});
  };

  const toggleOwned = (name) => {
    api("/owned", { method: "POST", body: JSON.stringify({ deck_name: name }) })
      .then((res) => setOwned(new Set(res.owned)))
      .catch(() => {});
  };

  const selected = history?.find((r) => r.id === selectedId);

  return (
    <div className="frame">
      <Header favCount={favorites.size} onFavClick={() => navigate("/?favs=1")} />

      {history === null ? (
        <p className="hint" data-testid="dashboard-loading">Pulling up your matchmaking reports…</p>
      ) : loadError ? (
        <div className="auth-card" data-testid="dashboard-error" style={{ marginTop: 40 }}>
          <h1>Something glitched</h1>
          <p className="sub">I couldn't load your matchmaking reports. That's a hiccup on my end, not a missing account. Give it another go.</p>
          <button className="stamp-btn solid" data-testid="dashboard-retry-button" onClick={loadHistory}>Try again</button>
        </div>
      ) : history.length === 0 ? (
        <section className="intro" data-testid="dashboard-empty">
          <div className="eyebrow">The Matchmaking Report</div>
          <h1 style={{ fontSize: "clamp(28px,7vw,36px)" }}>No matches yet, let's fix that</h1>
          <p className="lede">
            Your matchmaking reports live here. Every time you take the quiz, that round's matches
            get saved to this page, so you can always come back and revisit your BFFs.
          </p>
          <Link to="/" className="stamp-btn solid" data-testid="dashboard-start-quiz-link"
            style={{ display: "inline-block", textDecoration: "none" }}>
            Find My Oracle BFF
          </Link>
        </section>
      ) : (
        <section data-testid="dashboard-results">
          <div className="results-head">
            <div className="eyebrow">The Matchmaking Report</div>
            <h2>Your New BFF Circle</h2>
            <p>
              {history.length === 1
                ? "Your matches, saved right here. Retake the quiz any time. Each round gets its own report."
                : `You've taken the quiz ${history.length} times. Pick a date to revisit that round's matches.`}
            </p>
          </div>

          {history.length > 1 && (
            <div className="attempt-select-row" data-testid="attempt-select-row">
              <label htmlFor="attemptSelect">View my results for:</label>
              <select id="attemptSelect" data-testid="attempt-select" value={selectedId || ""}
                onChange={(e) => setSelectedId(e.target.value)}>
                {history.map((r, i) => (
                  <option key={r.id} value={r.id} data-testid={`attempt-option-${r.id}`}>
                    Quiz {history.length - i}: {formatDate(r.completed_at)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {history.length === 1 && (
            <p className="dash-note" data-testid="dashboard-single-note">
              Saved {formatDate(history[0].completed_at)}. Retake the quiz any time and each round gets its own report here.
            </p>
          )}

          {selected && (
            <div data-testid={`dashboard-result-${selected.id}`}>
              <div id="matchList">
                {selected.top3.map((m) => (
                  <MatchCard key={m.deck.name} d={m.deck} scored={m.scores} rankNum={m.rank} showRibbon
                    isFav={favorites.has(m.deck.name)} onToggleFav={toggleFav}
                    isOwned={owned.has(m.deck.name)} onToggleOwned={toggleOwned}
                    onCtaClick={(deck, rank) => {
                      api("/events", { method: "POST", body: JSON.stringify({ type: "cta_click", result_id: selected.id, deck_name: deck.name, rank }) }).catch(() => {});
                    }} />
                ))}
                {(selected.more_matches || []).map((m) => (
                  <MatchCard key={`more-${m.deck.name}`} d={m.deck} scored={m.scores} rankNum={m.rank} showRibbon={false}
                    isFav={favorites.has(m.deck.name)} onToggleFav={toggleFav}
                    isOwned={owned.has(m.deck.name)} onToggleOwned={toggleOwned} />
                ))}
              </div>
              <div className="activation-block">
                <h3>Activate Your New Oracle BFF</h3>
                <p>Meeting your match is just the start. The real magic happens once you two actually bond. My <i>Intuitive Oracle Activation 5-Day Challenge</i> shows you exactly how to make your cards come alive with intuitive hits so you feel connected and guided through every card pull, and every reading becomes an accurate, intuitive message you trust.</p>
                <a className="stamp-btn gold" data-testid="challenge-link" href="https://iloveoraclecards.com/" target="_blank" rel="noopener"
                  onClick={() => { api("/events", { method: "POST", body: JSON.stringify({ type: "challenge_click", result_id: selected.id }) }).catch(() => {}); }}>Take the 5-Day Challenge</a>
              </div>
              {(selected.extras || []).length > 0 && (
                <>
                  <div className="accessory-head">Pairs well with your matches</div>
                  {selected.extras.map((a) => (
                    <AccessoryCard key={a.name} a={a} isFav={favorites.has(a.name)} onToggleFav={toggleFav} />
                  ))}
                </>
              )}
            </div>
          )}

          <div className="results-actions">
            <Link to="/" className="retake" data-testid="dashboard-retake-link">Take the quiz again</Link>
          </div>
          <p className="amazon-note">Some links above are affiliate links. Jen may earn from qualifying purchases (at no extra cost to you).</p>
        </section>
      )}

      <Footer />
    </div>
  );
}
